import type { InjectionKey } from 'vue';
import { createStore, Store } from 'vuex';
import { safeClearLocalStorage } from '@/utils/safeClearStorage';

import vuexPersistedstate from 'vuex-persistedstate';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'rc4', isCompression: false });

const setVuexKey = 'modernize_v3_template';

export interface SocketState {
    isConnected: boolean;
    socketId: string | null;
    reconnectAttempts: number;
}

export interface State {
    user: any;
    token: null;
    socket: SocketState;
}

export const key: InjectionKey<Store<State>> = Symbol();

export const store = createStore<State>({
    state: {
        user: [],
        token: null,
        socket: {
            isConnected: false,
            socketId: null,
            reconnectAttempts: 0
        }
    },
    getters: {
        isLoggedIn(state) {
            return state.user;
        },
        isSocketConnected(state) {
            return state.socket.isConnected;
        },
        socketId(state) {
            return state.socket.socketId;
        }
    },
    mutations: {
        loginSuccess(state, payload) {
            state.user = payload;
        },
        logout(state) {
            state.user = [];
        },
        refreshUserData(state, payload) {
            state.user.user = payload;
        },
        renewToken(state, payload) {
            state.token = payload;
        },
        twoFactorSuccess(state, payload) {
            state.user.hasAuthFactor = null;
            state.user.isAuthFactorPass = true;
            state.user.token = payload.token;
            state.user.user = payload.user;
        },
        // Socket mutations
        SOCKET_CONNECTED(state, socketId: string) {
            state.socket.isConnected = true;
            state.socket.socketId = socketId;
            state.socket.reconnectAttempts = 0;
        },
        SOCKET_DISCONNECTED(state) {
            state.socket.isConnected = false;
            state.socket.socketId = null;
        },
        SOCKET_RECONNECT_ATTEMPT(state) {
            state.socket.reconnectAttempts++;
        },
        SOCKET_RECONNECT_RESET(state) {
            state.socket.reconnectAttempts = 0;
        }
    },
    actions: {
        async logout(context) {
            // Disconnect socket before logging out
            context.dispatch('disconnectSocket');
            // Clear ALL browser cache before logging out
            context.dispatch('clearAllBrowserCache');

            // Drop the per-account Pinia stores (plan flags, feature flags,
            // upload config, storage cache). clearAllBrowserCache() above only
            // clears localStorage/sessionStorage — these stores live in memory
            // for the lifetime of the PAGE and each gates its fetch behind a
            // `loaded` flag, so without this the next account to sign in keeps
            // this account's plan locks until the browser is refreshed.
            const { resetPerAccountStores } = await import('@/utils/resetPerAccountStores');
            await resetPerAccountStores();

            context.commit("logout");
        },

        // Socket actions
        async connectSocket(context) {
            try {
                const { socketService } = await import('@/services/socketService');
                const token = context.state.user?.token;
                if (token) {
                    await socketService.connect(token);
                    context.commit('SOCKET_CONNECTED', socketService.socketId);
                }
            } catch (error) {
                console.error('Failed to connect socket:', error);
            }
        },

        disconnectSocket(context) {
            import('@/services/socketService').then(({ socketService }) => {
                socketService.disconnect();
                context.commit('SOCKET_DISCONNECTED');
            }).catch(() => {
                // Socket service not loaded yet
            });
        },
        
        clearAllBrowserCache() {
            console.log('🧹 [Vuex] Clearing ALL localStorage items...');
            
            // Get all localStorage keys before clearing
            const allKeys = Object.keys(localStorage);
            console.log(`📋 [Vuex] Found ${allKeys.length} items in localStorage:`, allKeys);
            
            // Explicitly remove critical items first (for logging purposes)
            const criticalItems = [
                '_secure__ls__metadata',
                'user',
                'csrfToken',
                'security_logs',
                'modernize_v3_template'
            ];
            
            criticalItems.forEach(item => {
                if (localStorage.getItem(item)) {
                    localStorage.removeItem(item);
                    console.log(`🔥 [Vuex] Explicitly removed: ${item}`);
                }
            });
            
            // Clear everything from localStorage (preserves costings backups)
            safeClearLocalStorage();
            
            console.log('✅ [Vuex] All localStorage items cleared');
            
            // Also clear sessionStorage
            sessionStorage.clear();
            console.log('✅ [Vuex] SessionStorage cleared');
            
            // Clear the SecureLS encrypted storage
            ls.removeAll();
            console.log('✅ [Vuex] SecureLS storage cleared');
            
            // Clear any IndexedDB data if it exists
            if ('indexedDB' in window) {
                try {
                    const dbNames = ['webviewer', 'wc-cache', 'app-cache'];
                    dbNames.forEach(async (dbName) => {
                        try {
                            const deleteReq = indexedDB.deleteDatabase(dbName);
                            deleteReq.onsuccess = () => console.log(`✅ [Vuex] IndexedDB '${dbName}' cleared`);
                            deleteReq.onerror = () => console.log(`⚠️ [Vuex] IndexedDB '${dbName}' not found`);
                        } catch (e) {
                            console.log(`⚠️ [Vuex] Could not clear IndexedDB '${dbName}':`, e);
                        }
                    });
                } catch (e) {
                    console.log('⚠️ [Vuex] IndexedDB clearing failed:', e);
                }
            }
            
            console.log('🎯 [Vuex] All browser cache cleared successfully');
        }
    },
    plugins: [
        vuexPersistedstate({
            key: setVuexKey,
            storage: {
                getItem: (key) => {
                    try {
                        return ls.get(key);
                    } catch (e) {
                        // Corrupt/incompatible persisted state (e.g. wrong secure-ls
                        // secret/encoding, or metadata cleared) throws "Malformed UTF-8
                        // data" from CryptoJS during decrypt. Self-heal: drop the bad
                        // entry and boot with empty state instead of crashing the app.
                        console.warn('storeVuex: clearing corrupt persisted state', e);
                        try { ls.remove(key); } catch { /* ignore */ }
                        return null;
                    }
                },
                setItem: (key, value) => ls.set(key, value),
                removeItem: (key) => ls.remove(key)
            }
        })
    ]
});
